import { PRO_PAGE_ROUTE_PER_PRO_TYPE } from '@application/constants';
import type { Route } from '@application/types';
import type {
  NavigateToRouteOptions,
  ProSearchField,
  ProSearchInputField,
  ProSearchInputValues,
  ProSearchOptions,
  ProSearchParams,
  ProSearchValues,
} from '@application/utils';
import { encodeUri, useDebounce, usePlace, useRouter } from '@application/utils';
import { CenterTypeEnum, CountryCode, ProfessionalTypeEnum } from '@gql/graphql';
import { SearchOption } from '@presentation/components';
import { PLACEHOLDER_IMAGE } from '@presentation/constants';
import cleanDeep from 'clean-deep';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetPros } from './api/useGetPros';
import { useGetServices } from './api/useGetServices';

export * from './types';

export const useProSearch = (params?: ProSearchParams) => {
  const { initialValues, onSubmit } = params ?? {};

  const initialValuesFormatted = useMemo((): ProSearchValues => {
    return {
      proType: ProfessionalTypeEnum.VET,
      location: undefined,
      search: undefined,
      ...initialValues,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.proType, initialValues?.location, initialValues?.search]);

  const { navigateToRoute, language, translate, countryCode, infoFromCurrentPath } = useRouter();

  const [values, setValues] = useState<ProSearchValues>({ ...initialValuesFormatted });

  const [predictions, setPredictions] = useState<ProSearchOptions['location']>([]);

  const [tempValues, setTempValues] = useState<ProSearchValues>({
    location: undefined,
    search: undefined,
    proType: undefined,
  });

  const [inputValues, setInputValues] = useState<ProSearchInputValues>({
    location: undefined,
    search: undefined,
  });

  // Reset on proType change
  useEffect(() => {
    setValues({
      ...values,
      proType: initialValuesFormatted.proType,
      search: undefined,
    });
    setInputValues({
      ...inputValues,
      search: undefined,
    });
    setTempValues({
      ...tempValues,
      search: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesFormatted.proType]);

  // Reset on proType change
  useEffect(() => {
    setValues({
      ...values,
      proType: values.proType,
      search: undefined,
    });
    setInputValues({
      ...inputValues,
      search: undefined,
    });
    setTempValues({
      ...tempValues,
      search: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.proType]);

  // [ALL] Get service's url and location address from the url
  const paramValues = useMemo((): Partial<ProSearchInputValues> => {
    const [firstParam, secondParam] = infoFromCurrentPath.params;
    if (!firstParam) return {};
    if (firstParam === 'service') return { search: secondParam };
    return { location: decodeURI(firstParam), search: secondParam };
  }, [infoFromCurrentPath.params]);

  // [LOCATION] On params change (page load), directly set the input value to the param value
  useEffect(() => {
    if (paramValues.location) {
      setInputValues({ ...inputValues, location: decodeURI(paramValues.location) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramValues]);

  // [LOCATION] On params change (page load), or on values change, fetch the google place
  const { place, getAutocompletePredictions, suggestionsError, suggestionsLoading } = usePlace({
    input: paramValues.location,
  });

  // [LOCATION] When the google place changes, set the value.location to the found place.id
  useEffect(() => {
    setValues({ ...values, location: place?.placeId ?? undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place?.placeId]);

  // [SEARCH] API used for input field options
  const { services, servicesLoading, servicesGetUrl } = useGetServices({
    searchString: values.proType === ProfessionalTypeEnum.VET ? inputValues.search : undefined,
    serviceId: values.proType === ProfessionalTypeEnum.VET ? tempValues.search : undefined,
    proType: values.proType,
  });

  // [SEARCH] API used for title, initial value of the service
  const { services: initialServices, servicesLoading: initialServicesLoading } = useGetServices({
    searchString: values.proType === ProfessionalTypeEnum.VET ? paramValues.search : undefined,
    proType: values.proType,
  });

  // [SEARCH] Update service on values.search change
  useEffect(() => {
    if (initialServices.length) {
      const foundService = initialServices.find((x) => servicesGetUrl(x) === paramValues.search);
      if (foundService) {
        setValues({ ...values, search: foundService.id });
        setTempValues({ ...tempValues, search: foundService.id });
        setInputValues({
          ...inputValues,
          search:
            foundService?.translations?.find((x) => x.language === language)?.title ?? undefined,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialServices?.[0]?.id]);

  // [SEARCH] API used for input field options
  const { pros, prosLoading } = useGetPros({
    searchString: inputValues.search,
    skip: !inputValues.search || !values.proType,
    type: values.proType ?? ProfessionalTypeEnum.VET,
  });

  // [ALL] Html on change for the fields
  const fieldChangeHandler = useCallback(
    (field: ProSearchInputField) => {
      setInputValues({ ...inputValues, [field.type]: field.value });
    },
    [inputValues],
  );

  // [ALL] Select handler for the dropdown options
  const fieldSelectHandler = useCallback(
    (field: ProSearchField) => {
      // This logic is the search select handler, but only for pros
      // Pros are redirected straight to their page
      if (field.type === 'search' && field.value?.label && typeof field.value.label !== 'string') {
        const foundPro = pros.find((x) => x.id === field.value?.value);
        let translations = (foundPro?.translations ?? []).find((x) => x.language === language);
        if (!translations) {
          translations = (foundPro?.translations ?? []).find((x) => x);
        }
        if (!foundPro?.type) return;
        navigateToRoute(PRO_PAGE_ROUTE_PER_PRO_TYPE[foundPro?.type], {
          params: [translations?.url ?? ''],
        });
        return;
      }

      setTempValues({ ...tempValues, [field.type]: field.value?.value ?? undefined }); // Set temp value
      setInputValues({ ...inputValues, [field.type]: field.value?.label?.toString() }); // Set the click label as input
      if (field.type === 'search' && field.value === undefined) {
        setValues({ ...values, search: undefined });
      }

      if (field.type === 'proType') {
        setValues({ ...tempValues, [field.type]: field.value?.value ?? undefined });
      }
    },
    [inputValues, language, navigateToRoute, pros, tempValues, values],
  );

  const submit = useCallback(async () => {
    let route: Route = 'vetList';
    if (values.proType === ProfessionalTypeEnum.GROOMER) route = 'groomerList';
    else if (values.proType === ProfessionalTypeEnum.EDUCATOR) route = 'educatorList';
    const searchParams: string[] = [];
    const address = inputValues.location;
    if (address) {
      // Check if input, and if placeId is set
      searchParams.push(encodeUri(address)); // Set the address in the url
    } else if (tempValues.search) {
      searchParams.push('service'); // If no address but service, prefix url with service
    }
    if (tempValues.search) {
      // Set the service in the url, second param always
      const foundService = services.find((x) => x.id === tempValues.search ?? values.search);
      if (!foundService) return;
      const url = servicesGetUrl(foundService);
      if (!url) return;
      searchParams.push(url);
    }
    onSubmit?.(tempValues);
    navigateToRoute(route, { params: searchParams });
  }, [
    inputValues.location,
    navigateToRoute,
    onSubmit,
    services,
    servicesGetUrl,
    tempValues,
    values.proType,
    values.search,
  ]);

  const submitWithoutLocation = useCallback(
    async (navigationOptions: NavigateToRouteOptions) => {
      setInputValues({ ...inputValues, location: undefined });
      setValues({ ...values, location: undefined });
      setTempValues({ ...tempValues, location: undefined });
      let route: Route = 'vetList';
      if (values.proType === ProfessionalTypeEnum.GROOMER) route = 'groomerList';
      else if (values.proType === ProfessionalTypeEnum.EDUCATOR) route = 'educatorList';
      const searchParams: string[] = [];
      if (tempValues.search) {
        searchParams.push('service'); // If no address but service, prefix url with service
      }
      if (tempValues.search) {
        // Set the service in the url, second param always
        const foundService = services.find((x) => x.id === tempValues.search ?? values.search);
        if (!foundService) return;
        const url = servicesGetUrl(foundService);
        if (!url) return;
        searchParams.push(url);
      }
      navigateToRoute(route, {
        params: searchParams,
        ...navigationOptions,
      });
    },
    [inputValues, navigateToRoute, services, servicesGetUrl, tempValues, values],
  );

  const selectOptionsSearch = useMemo(() => {
    const servicesOptions = services
      .filter((service) => service.translations.find((x) => x.title))
      .map((service) => {
        let translations = (service?.translations ?? []).find((x) => x.language === language);
        if (!translations) {
          translations = (service?.translations ?? []).find((x) => x);
        }
        return {
          type: 'service',
          option: {
            label: translations?.title,
            value: service.id,
          },
        };
      });
    const proOptions = pros.map((pro) => {
      const centerType = pro.centerType ?? CenterTypeEnum.CABINET;
      const centerTypeFormatted = translate(`enum.centerType.${centerType}`);
      const location = cleanDeep([
        pro.centerType && centerTypeFormatted,
        pro.address?.city ?? undefined,
      ]);
      let translations = (pro?.translations ?? []).find((x) => x.language === language);
      if (!translations) {
        translations = (pro?.translations ?? []).find((x) => x);
      }
      return {
        type: 'pro',
        option: {
          value: pro.id,
          ignoreSearch: true,
          label: (
            <SearchOption
              title={translations?.title ?? ''}
              subtitle={location.join(' - ')}
              image={pro?.imgs[0]?.medium ?? PLACEHOLDER_IMAGE}
            />
          ),
        },
      };
    });
    return [...servicesOptions, ...proOptions];
  }, [language, pros, services, translate]);

  const selectOptionsProType = useMemo((): ProSearchOptions['proType'] => {
    return [
      { value: ProfessionalTypeEnum.VET, label: translate('glossary.vet') },
      {
        value: ProfessionalTypeEnum.GROOMER,
        label: translate('glossary.groomer'),
      },
      {
        value: ProfessionalTypeEnum.EDUCATOR,
        label: translate('glossary.educator'),
      },
    ];
  }, [translate]);

  const debouncedLocationSearch = useDebounce(inputValues.location, 250);
  useEffect(() => {
    const fetch = async () => {
      const result = await getAutocompletePredictions({
        search: debouncedLocationSearch,
        ...(countryCode !== CountryCode.EU ? { country: countryCode } : {}),
      });
      if (result?.length) {
        setPredictions(
          result.map((prediction) => {
            return {
              value: String(prediction.placeId),
              label: prediction.label,
            };
          }),
        );
      }
    };

    fetch();
  }, [translate, debouncedLocationSearch, getAutocompletePredictions, countryCode]);

  const selectOptions = useMemo((): ProSearchOptions => {
    return {
      proType: selectOptionsProType,
      search: selectOptionsSearch.map((x) => x.option),
      location: predictions,
    };
  }, [predictions, selectOptionsProType, selectOptionsSearch]);

  const serviceTitle = useMemo((): string | undefined => {
    let service = initialServices.find((x) =>
      x.translations.find((y) => y.url === paramValues.search),
    );
    if (!service) {
      service = initialServices.find((x) => x.id === values.search);
    }
    if (!service) return;
    return (
      service.translations?.find((x) => x.language === language && x.title)?.title ?? undefined
    );
  }, [initialServices, language, paramValues.search, values.search]);

  return {
    values, // Values submitted
    tempValues, // Values not submitted yet
    inputValues, // Values of the input search
    loading: initialServicesLoading || servicesLoading || prosLoading,
    fieldChangeHandler,
    fieldSelectHandler,
    submit,
    options: selectOptions,
    place,
    params: paramValues,
    serviceTitle,
    submitWithoutLocation,
    suggestionsError,
    suggestionsLoading,
  };
};
